// @flow
import { Backbone } from "extensions/backbone";
import { _ } from "extensions/underscore";
import Radio from "backbone.radio";

import { ColumnsCollection } from "common/components/table/columnsCollection";
import { PagerModel } from "common/components/table/pagerModel";

type ColumnType = Object;

export const SmartTableModel = Backbone.Model.extend({
    defaults: {
        fixed: true,
        grow1: false,
        showLoadMore: false,
        showHeader: true,
        pager: false,
        mode: "server",
        height: "auto",
        noDataMessage: "There is no data available for the selected date range"
    },
    
    initialize() {
        this.pagerModel = new PagerModel({
            enabled: this.get("pager")
        });
        const allColumns = this.flattenGroupedColumns(this.getColumns());
        const staticColumns = [];
        const lockedColumns = [];
        
        allColumns.forEach((column) => {
            if (!column.locked) {
                staticColumns.push(column);
            } else {
                lockedColumns.push(column);
            }
        });
        
        this.columns = new ColumnsCollection(allColumns);
        this.lockedColumns = new ColumnsCollection(lockedColumns);
        this.staticColumns = new ColumnsCollection(staticColumns);
        
        const groupingColumns = this.getGroupingColumns();
        this.groupColumns = new ColumnsCollection(groupingColumns);
    },
    
    getColumns({ predicate = (column, index) => _.result(column, "visible", true) } = {}): Array<ColumnType> {
        return this.get("columns").reduce((memo, column, index) => {
            if (predicate(column, index)) {
                // Expand the column if it contains a reference to a getter
                // TODO: We need to fix this server-side. Some txns have "external_ref_id" but some have "transaction_id" attr
                // Here we don't apply getter if it's external_ref_id getter
                if (Object.prototype.hasOwnProperty.call(column, "getter") && column.getter !== "5b19997edabe69133a95efaa") {
                    const getter = Radio.channel("main").request("get:getters").findById(column.getter);
                    if (getter) {
                        memo.push({
                            ...column,
                            name: getter.name,
                            attribute: getter.key,
                            cell: getter.valueType,
                            cellOptions: {
                                precision: getter.decimals,
                                ...column.cellOptions
                            }
                        });
                    } else {
                        throw new Error(`Unable to find requested getter: ${column.getter}`);
                    }
                } else {
                    memo.push(column);
                }
            }
            
            return memo;
        }, []);
    },
    
    getExportGetters({ predicate = _.constant(true) } = {}) {
        return this.getColumns({ predicate }).reduce((memo, column) => {
            if (Object.prototype.hasOwnProperty.call(column, "exportOptions") && column.exportOptions.getter) {
                if (Array.isArray(column.exportOptions.getter)) {
                    memo.push(...column.exportOptions.getter);
                } else {
                    memo.push(column.exportOptions.getter);
                }
            } else if (column.getter) {
                memo.push(column.getter);
            } else if (typeof column.attribute === "string" && column.cell !== "checkbox") {
                memo.push(column.attribute);
            }
            
            return memo;
        }, []);
    },
    
    resetState() {
        this.pagerModel.resetState();
    },
    
    flattenGroupedColumns(allColumns): Array<ColumnType> {
        const getColumns = function(nodes) {
            let columns = [];
            
            nodes.forEach((node) => {
                if (node.columns) {
                    columns = columns.concat(getColumns(node.columns));
                } else {
                    columns.push(node);
                }
            });
            
            return columns;
        };
        
        return getColumns(allColumns);
    },
    
    /**
     * Check if the table contains locked columns
     * @returns {boolean}
     */
    hasLockedColumns(): boolean {
        return !!this.lockedColumns.length;
    },
    
    hasGroupingColumns(): boolean {
        return !!this.groupColumns.length;
    },
    
    isScrollable(): boolean {
        return this.get("fixed") === false;
    },
    
    growFirstColumn(): boolean {
        return this.get("grow1") === true;
    },
    
    getGroupingColumns(): Array<ColumnType> {
        return this.getColumns().reduce((memo, column) => {
            if (column.columns) {
                const clone = { ...column };
                delete clone.columns;
                
                clone.header = {
                    attributes: {
                        colspan: column.columns.length
                    }
                };
                memo.push(clone);
            }
            return memo;
        }, []);
    }
});
